import { chunk } from 'lodash-es'

import { PlaybackTelemetry } from '..'
import { StoreRecord } from '../types'
import { EVENT_PLAYBACK_STATUS } from '../enums'
import { TELEMETRY_INDEXES } from '../utils'

import { VIDEO_TYPE } from './enums'

import { StorageSchema } from '../../../utils/storage'

import {
  Chapter,
  VideoChapterEndedTelemetryEvent,
  VideoChapterStartedTelemetryEvent,
  VideoEndedTelemetryEvent,
  VideoProgressedTelemetryEvent,
  VideoRangeEndedTelemetryEvent,
  VideoRangeStartedTelemetryEvent,
  VideoStartedTelemetryEvent
} from './events'
import { VideoPlaybackTelemetryAPI } from '../../api/playback/telemetry'
import { HTTPRequestError, MissingTelemetryEventsError } from '../../../errors'

export type VideoEvent = VideoStartedTelemetryEvent |
VideoEndedTelemetryEvent | VideoChapterStartedTelemetryEvent |
VideoChapterEndedTelemetryEvent | VideoRangeStartedTelemetryEvent |
VideoRangeEndedTelemetryEvent | VideoProgressedTelemetryEvent

const TELEMETRY_EVENTS_CHUNK_SIZE = 10

export const isPending = (reason?: EVENT_PLAYBACK_STATUS) => reason === EVENT_PLAYBACK_STATUS.PENDING
export const isSending = (reason?: EVENT_PLAYBACK_STATUS) => reason === EVENT_PLAYBACK_STATUS.SENDING

export const byType = (record: StoreRecord<VideoEvent>) => !isPending(record.event.data['reason' as never])
export const byRequestStatusSending = (record: StoreRecord<VideoEvent>) => !isSending(record.event.data['requestStatus' as never])

interface Schema extends StorageSchema {
  video: {
    key: number,
    value: StoreRecord<VideoEvent>
  }
}

export type VideoContent = {
  videoType: VIDEO_TYPE
  productId: string
  releaseId: string
}

export class VideoPlaybackTelemetry extends PlaybackTelemetry<VideoContent, Schema, VideoEvent> {
  #api = new VideoPlaybackTelemetryAPI('v2')

  public async save<T>(data: T) {
    await this.create()
    await this.storage.stores.video.save(data, data['id' as never])
  }

  public get api(): VideoPlaybackTelemetryAPI {
    return this.#api
  }

  public async update(event: VideoEvent) {
    const index = this.replace(event)
    const payload = this.store[index]

    await this.save(payload)
  }

  public async sync() {
    await this.storage.stores.video.clear()
    await this.storage.stores.video.save(this.store)
  }

  public async started() {
    const [event, payload] = this.push(new VideoStartedTelemetryEvent)

    if (this.config.autosave) await this.save(payload)

    return event as VideoEvent
  }

  public async progressed(
    position: number,
    maxPosition: number,
    progress: number,
    duration: number,
    status = EVENT_PLAYBACK_STATUS.PLAYING,
    chapter?: Chapter
  ) {

    const [event, payload] = this.push(new VideoProgressedTelemetryEvent(position, maxPosition, progress, duration, status, chapter))

    if (this.config.autosave) await this.save(payload)

    return event as VideoEvent
  }

  public async ended(
    position: number,
    maxPosition: number,
    progress: number,
    duration: number,
    reason = EVENT_PLAYBACK_STATUS.COMPLETED,
    chapter?: Chapter
  ) {

    const [event, payload] = this.push(new VideoEndedTelemetryEvent(position, maxPosition, progress, duration, reason, chapter))

    if (this.config.autosave) await this.save(payload)

    return event as VideoEvent
  }

  public async chapterStarted(chapter: Chapter) {
    const [event, payload] = this.push(new VideoChapterStartedTelemetryEvent(chapter))

    if (this.config.autosave) await this.save(payload)

    return event as VideoEvent
  }

  public async chapterEnded(
    position: number,
    maxPosition: number,
    duration: number,
    progress: number,
    chapter: Chapter,
    reason = EVENT_PLAYBACK_STATUS.PENDING) {
    const [event, payload] = this.push(
      new VideoChapterEndedTelemetryEvent(position, maxPosition, duration, progress, chapter, reason)
    )

    if (this.config.autosave) await this.save(payload)

    return event as VideoEvent
  }

  public async rangeStarted(start: number) {
    const [event, payload] = this.push(new VideoRangeStartedTelemetryEvent(start))

    if (this.config.autosave) await this.save(payload)

    return event as VideoEvent
  }

  public async rangeEnded(start: number, end: number, duration: number, reason = EVENT_PLAYBACK_STATUS.PENDING) {
    const [event, payload] = this.push(new VideoRangeEndedTelemetryEvent(start, end, duration, reason))

    if (this.config.autosave) await this.save(payload)

    return event as VideoEvent
  }

  async #fetch(events: StoreRecord<VideoEvent>[], ignoreIndex: boolean) {
    if (!this.session || !this.storage) throw new Error('Missing session and storage')

    const index = {
      ...TELEMETRY_INDEXES[1],
      ...{ options: undefined, value: this.session.id }
    }

    try {
      for (const item of events) {
        item.event.data['requestStatus'] = EVENT_PLAYBACK_STATUS.SENDING
        this.replace(item.event)
      }

      const invalid = await this.api.send(events.map(i => i.event))
      const list = invalid.length ? invalid : events.map(event => event.id)

      this.remove(list)
      await this.storage.stores.video.remove(list, ignoreIndex ? undefined : index)

      return true
    } catch (error) {
      if (error instanceof HTTPRequestError) {
        this.save(events.map(i => {
          i.status = EVENT_PLAYBACK_STATUS.FAILED
          return i
        }))
      } else {
        console.warn('Storage error:', error)
      }
      return false
    }
  }

  public async sendStored(complete = true, reset = false) {
    if (reset) this.reset()

    await this.create()

    const events: Array<StoreRecord<VideoEvent>> = await this.storage.stores.video.all()

    const list = events.map(i => {
      if (isPending(i.event.data['reason' as never]) && complete)
        i.event.data['reason'] = EVENT_PLAYBACK_STATUS.COMPLETED
      return i
    })

    await this.send(list, true)
  }

  public async send(list?: Array<StoreRecord<VideoEvent>>, ignoreIndex = false) {
    const events = chunk(list?.filter(byRequestStatusSending) || this.store.filter(byType).filter(byRequestStatusSending), TELEMETRY_EVENTS_CHUNK_SIZE)
    if (!events.length) throw new MissingTelemetryEventsError

    await Promise.all(events.map(butch => this.#fetch(butch, ignoreIndex)))
  }
}
